import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import ExcelUploadModal from '../ExcelUploadModal';

// Funkce pro formátování čísel s mezerami jako oddělovači tisíců
const formatNumber = (num) => {
  return new Intl.NumberFormat('cs-CZ', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(num);
};

export default function CashbackTab({ userRole, users, currentUserId, isDarkMode }) {
  const [cashbacks, setCashbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(userRole === 'admin' ? '' : currentUserId);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [manualCashback, setManualCashback] = useState({
    companyName: '',
    ico: '',
    amount: '',
    reason: '',
    description: '',
    userId: '',
    month: getCurrentMonth()
  });



  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  const fetchCashbacks = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/cashbacks?month=${selectedMonth}&userId=${currentUserId}`;
      if (selectedUser) {
        url += `&filterUserId=${selectedUser}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCashbacks(data.cashbacks || []);
      }
    } catch (error) {
      console.error('Chyba při načítání cashbacků:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch(`${API_URL}/cashbacks/months?userId=${currentUserId}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMonths(data.months || []);
      }
    } catch (error) {
      console.error('Chyba při načítání měsíců:', error);
    }
  };



  const deleteCashback = async (cashbackId) => {
    if (!window.confirm('Opravdu chcete smazat tento cashback?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/cashbacks/${cashbackId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchCashbacks();
      } else {
        alert('Chyba při mazání cashbacku');
      }
    } catch (error) {
      alert('Chyba při mazání cashbacku');
      console.error('Chyba při mazání cashbacku:', error);
    }
  };

  const assignUserToCashback = async (cashbackId, userId) => {
    try {
      const response = await fetch(`${API_URL}/cashbacks/${cashbackId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        await fetchCashbacks();
      } else {
        alert('Chyba při přiřazování uživatele');
      }
    } catch (error) {
      console.error('Chyba při přiřazování uživatele:', error);
      alert('Chyba při přiřazování uživatele');
    }
  };

  const deleteMonth = async () => {
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    const confirmMessage = `Opravdu chcete smazat všechny cashback/srážky pro měsíc ${monthName}? Tato akce je nevratná!\n\nPočet záznamů ke smazání: ${cashbacks.length}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cashbacks/month/${selectedMonth}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Úspěšně smazáno ${data.deletedCount} cashback/srážek pro měsíc ${monthName}`);
        await fetchCashbacks();
        await fetchAvailableMonths();
      } else {
        const errorData = await response.json();
        alert('Chyba při mazání: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při mazání měsíce');
      console.error('Chyba při mazání měsíce:', error);
    } finally {
      setLoading(false);
    }
  };

  const addManualCashback = async () => {
    if (!manualCashback.companyName || !manualCashback.amount || !manualCashback.reason || !manualCashback.userId) {
      alert('Vyplňte prosím název firmy, částku, důvod a vyberte uživatele');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/cashbacks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: manualCashback.companyName,
          ico: manualCashback.ico || '',
          amount: parseFloat(manualCashback.amount),
          reason: manualCashback.reason,
          description: manualCashback.description || '',
          userId: manualCashback.userId,
          month: manualCashback.month
        })
      });

      if (response.ok) {
        alert('Cashback byl úspěšně přidán');
        setShowManualAddModal(false);
        setManualCashback({
          companyName: '',
          ico: '',
          amount: '',
          reason: '',
          description: '',
          userId: '',
          month: getCurrentMonth()
        });
        fetchCashbacks();
      } else {
        const errorData = await response.json();
        alert('Chyba při přidávání cashbacku: ' + (errorData.error || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při přidávání cashbacku:', error);
      alert('Chyba při přidávání cashbacku');
    }
  };

  useEffect(() => {
    fetchCashbacks();
    fetchAvailableMonths();
  }, [selectedMonth, selectedUser]);

  // Výpočet souhrnů
  const totalCashback = cashbacks.reduce((sum, c) => sum + (c.amount || 0), 0);
  
  // Souhrn podle důvodů
  const reasonSummary = cashbacks.reduce((acc, cashback) => {
    const reason = cashback.reason || 'Neurčeno';
    if (!acc[reason]) {
      acc[reason] = { count: 0, total: 0 };
    }
    acc[reason].count += 1;
    acc[reason].total += cashback.amount || 0;
    return acc;
  }, {});



  return (
    <div>
      <div className={`mb-6 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow-sm border`}>
        <div className="flex items-center gap-4">
          <div>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mr-2`}>Měsíc:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className={`border rounded px-3 py-1 text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
            >
              <option value={getCurrentMonth()}>Aktuální měsíc</option>
              {[...availableMonths].reverse().map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          {userRole === 'admin' && (
            <>
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mr-2`}>Uživatel:</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className={`border rounded px-3 py-1 text-sm min-w-[150px] ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Všichni uživatelé</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium"
              >
                📤 Nahrát Excel
              </button>
              <button
                onClick={() => setShowManualAddModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
              >
                ➕ Přidat cashback ručně
              </button>
              <button
                onClick={fetchCashbacks}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm disabled:opacity-50 mr-2"
              >
                {loading ? 'Načítání...' : 'Obnovit'}
              </button>
              {cashbacks.length > 0 && (
                <button
                  onClick={deleteMonth}
                  disabled={loading}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                >
                  🗑️ Smazat celý měsíc
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Souhrny */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-red-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Celkové srážky</h3>
          <p className="text-2xl font-bold text-red-600">{formatNumber(totalCashback)} Kč</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-blue-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Počet záznamů</h3>
          <p className="text-2xl font-bold text-blue-600">{formatNumber(cashbacks.length)}</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-gray-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Důvodů</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatNumber(Object.keys(reasonSummary).length)}</p>
        </div>
      </div>

      {/* Souhrn podle důvodů */}
      {Object.keys(reasonSummary).length > 0 && (
        <div className={`mb-6 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow`}>
          <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Souhrn podle důvodů</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(reasonSummary).map(([reason, data]) => (
              <div key={reason} className={`border rounded p-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{reason}</div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{data.count} záznamů</div>
                <div className="font-semibold text-red-600">{formatNumber(data.total)} Kč</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabulka */}
      {loading ? (
        <div className="text-center py-8">Načítání cashbacků...</div>
      ) : (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          {cashbacks.length === 0 ? (
            userRole === 'admin' ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádné cashbacky/srážky pro vybraný měsíc.
                <br />
                Použijte tlačítko "📤 Nahrát Excel" pro import dat.
              </div>
            ) : null
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Název firmy</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>IČO</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Důvod</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Částka</th>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Popis</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Datum</th>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Uživatel</th>
                    {userRole === 'admin' && (
                      <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Akce</th>
                    )}
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {cashbacks.map((cashback) => (
                    <tr key={cashback._id} className={`${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-50'}`}>
                      <td className={`px-3 py-2 text-sm ${isDarkMode ? 'text-white' : ''}`}>{cashback.companyName}</td>
                      <td className={`px-3 py-2 text-sm text-center font-mono ${isDarkMode ? 'text-white' : ''}`}>{cashback.ico || '-'}</td>
                      <td className="px-3 py-2 text-sm text-center">
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                          {cashback.reason}
                        </span>
                      </td>
                      <td className={`px-3 py-2 text-sm text-center font-bold text-red-600`}>
                        {cashback.amount ? formatNumber(cashback.amount) : '0'} Kč
                      </td>
                      <td className={`px-3 py-2 text-sm max-w-xs ${isDarkMode ? 'text-white' : ''}`}>
                        <div className="truncate" title={cashback.description}>
                          {cashback.description || '-'}
                        </div>
                      </td>
                      <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-white' : ''}`}>
                        {cashback.createdAt ? 
                          new Date(cashback.createdAt).toLocaleDateString('cs-CZ') : 
                          '-'
                        }
                      </td>
                      <td className={`px-3 py-2 text-sm ${isDarkMode ? 'text-white' : ''}`}>
                        {cashback.userId ? (
                          <span className="text-blue-600">{cashback.userName || 'Uživatel'}</span>
                        ) : (
                          <select 
                            className={`text-sm border rounded px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
                            onChange={(e) => {
                              if (e.target.value) {
                                assignUserToCashback(cashback._id, e.target.value);
                              }
                            }}
                            defaultValue=""
                          >
                            <option value="" disabled>Vyberte uživatele...</option>
                            {users.map(user => (
                              <option key={user._id} value={user._id}>
                                {user.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      {userRole === 'admin' && (
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => {
                              if (window.confirm(`Opravdu chcete smazat cashback pro firmu "${cashback.companyName}"?`)) {
                                deleteCashback(cashback._id);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-sm"
                            title="Smazat"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}



      {/* Excel Upload Modal */}
      {showUploadModal && (
        <ExcelUploadModal
          selectedMonth={selectedMonth}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchCashbacks();
            fetchAvailableMonths();
          }}
          type="cashback"
        />
      )}

      {/* Manual Add Cashback Modal */}
      {showManualAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}>
            <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Přidat cashback ručně
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Název firmy *
                </label>
                <input
                  type="text"
                  value={manualCashback.companyName}
                  onChange={(e) => setManualCashback({...manualCashback, companyName: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. ABC s.r.o."
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  IČO
                </label>
                <input
                  type="text"
                  value={manualCashback.ico}
                  onChange={(e) => setManualCashback({...manualCashback, ico: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="12345678"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Částka (Kč) *
                </label>
                <input
                  type="number"
                  value={manualCashback.amount}
                  onChange={(e) => setManualCashback({...manualCashback, amount: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. -5000"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Důvod *
                </label>
                <input
                  type="text"
                  value={manualCashback.reason}
                  onChange={(e) => setManualCashback({...manualCashback, reason: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. Storno"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Popis
                </label>
                <textarea
                  value={manualCashback.description}
                  onChange={(e) => setManualCashback({...manualCashback, description: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Volitelný popis..."
                  rows="2"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Uživatel *
                </label>
                <select
                  value={manualCashback.userId}
                  onChange={(e) => setManualCashback({...manualCashback, userId: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Vyberte uživatele</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Měsíc *
                </label>
                <input
                  type="month"
                  value={manualCashback.month}
                  onChange={(e) => setManualCashback({...manualCashback, month: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowManualAddModal(false);
                  setManualCashback({
                    companyName: '',
                    ico: '',
                    amount: '',
                    reason: '',
                    description: '',
                    userId: '',
                    month: getCurrentMonth()
                  });
                }}
                className={`px-4 py-2 rounded text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Zrušit
              </button>
              <button
                onClick={addManualCashback}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
              >
                Přidat cashback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}